import React from 'react'

/**
 * Reusable LoadingSpinner component with consistent styling
 * Replaces all hardcoded loading states throughout the application
 * Supports different sizes, colors, and custom messages
 */
const LoadingSpinner = ({
  size = 'medium',
  color = 'blue',
  text = 'Loading...',
  className = '',
  showText = true,
  fullScreen = false,
  ...props
}) => {
  // Size classes for spinner
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-8 w-8',
    large: 'h-12 w-12',
    xl: 'h-16 w-16'
  }

  // Color classes for spinner
  const colorClasses = {
    blue: 'border-blue-500',
    green: 'border-green-500',
    red: 'border-red-500',
    gray: 'border-gray-500',
    white: 'border-white'
  }

  // Text size classes
  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
    xl: 'text-xl'
  }

  // Container classes
  const containerClasses = fullScreen 
    ? 'min-h-screen flex items-center justify-center' 
    : 'flex items-center justify-center'

  const spinnerClasses = `
    animate-spin rounded-full border-b-2 
    ${sizeClasses[size]} 
    ${colorClasses[color]}
  `.trim()

  const textClasses = `
    text-gray-600 mt-2
    ${textSizeClasses[size]}
  `.trim()

  return (
    <div className={`${containerClasses} ${className}`} {...props}>
      <div className="text-center">
        <div className={`${spinnerClasses} mx-auto`} />
        {showText && text && (
          <p className={textClasses}>
            {text}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * LoadingSpinner variants for common use cases
 */
export const LoadingSpinnerVariants = {
  // Full screen loading
  FullScreen: (props) => (
    <LoadingSpinner 
      size="large" 
      fullScreen 
      text="Loading..." 
      {...props} 
    />
  ),

  // Inline loading for buttons
  Inline: (props) => (
    <LoadingSpinner 
      size="small" 
      showText={false} 
      {...props} 
    />
  ),

  // Card loading
  Card: (props) => (
    <LoadingSpinner 
      size="medium" 
      text="Loading data..." 
      {...props} 
    />
  ),

  // Page loading
  Page: (props) => (
    <LoadingSpinner 
      size="large" 
      text="Loading page..." 
      {...props} 
    />
  ),

  // Table loading
  Table: (props) => (
    <LoadingSpinner 
      size="medium" 
      text="Loading table data..." 
      {...props} 
    />
  )
}

export default LoadingSpinner
